import { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { PAGE_TYPE, BROWSE_TYPES } from 'dibs-constants/exports/pageTypes';
import { FormattedMessage } from 'dibs-react-intl';
import { trackEvent } from 'dibs-tracking';

import Caret from 'dibs-icons/exports/ArrowUp';

// Helpers
import classnames from 'classnames';
import isPageType from '../utils/isPageType';
import throttle from 'lodash.throttle';
import { backToTopButtonId } from './backToTopConstants';

// Styles
import styles from './BackToTop.scss';

const PAGE_TYPE_WHITELIST = [...BROWSE_TYPES, PAGE_TYPE.FAVORITES, PAGE_TYPE.SEARCH];
const SCROLL_HEIGHT = 400;
const DURATION_THROTTLE_SCROLL_EVENTS = 100;

export const BackToTop = ({ showChatWidget, isCopyEnabled }) => {
    const [showScrollTop, setShowScrollTop] = useState(false);

    const handleScroll = throttle(
        useCallback(() => {
            const positionY = window.scrollY || window.pageYOffset;
            if (positionY > SCROLL_HEIGHT && !showScrollTop) {
                setShowScrollTop(true);
            } else if (positionY < SCROLL_HEIGHT && showScrollTop) {
                setShowScrollTop(false);
            }
        }, [showScrollTop]),
        DURATION_THROTTLE_SCROLL_EVENTS
    );

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [handleScroll]);

    const containerClasses = classnames(styles.backToTop, {
        [styles.positionAboveChat]: showChatWidget,
        [styles.isVisible]: showScrollTop,
    });
    const caretClasses = classnames(styles.caret, {
        [styles.caretWithoutCopy]: !isCopyEnabled,
        [styles.caretWithCopy]: isCopyEnabled,
    });

    const pageTypeValid = isPageType(PAGE_TYPE_WHITELIST);

    if (!pageTypeValid) {
        return null;
    }

    return (
        <div
            id={backToTopButtonId}
            className={containerClasses}
            onClick={() => {
                trackEvent({
                    category: 'results page refinement',
                    action: 'navigation',
                    label: 'back to top',
                });
                window.scroll({
                    top: 0,
                    behavior: 'smooth',
                });
            }}
            style={{
                // this is controlled by inline style so we can manipulate programatically without computed styles ()
                // see SbSharedStickyFollowSearchBanner.jsx in ABF for example
                bottom: showChatWidget ? '100px' : '20px',
            }}
        >
            <div className={caretClasses}>
                <Caret />
            </div>
            {isCopyEnabled && (
                <div className={styles.top}>
                    <FormattedMessage id="dbl.BackToTop.top" defaultMessage="TOP" />
                </div>
            )}
        </div>
    );
};

BackToTop.defaultProps = {
    showChatWidget: false,
    isCopyEnabled: true,
};

BackToTop.propTypes = {
    isCopyEnabled: PropTypes.bool,
    showChatWidget: PropTypes.bool,
};
